import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  computed
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { BusquedaPaginadorComponent } from '../busquedas/busqueda-paginador.component';
import {
  Poblacion,
  PoblacionFiltro
} from '../../../domain/model/transporte/poblacion';
import { PoblacionRepository } from '../../../domain/service/transporte/poblacion-repository';
import {
  sanitizeText,
  sanitizeTextPreserveSpaces
} from 'src/app/common/input-utils';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS
} from '../../../common/constants-ejb';
import { firstValueFrom } from 'rxjs';
import { buscadorPoblacionPreset } from 'src/app/presentation/shared/table-standardization/table-standard-presets';

/**
 * Componente reutilizable: Buscador de Población/Tarifa de Transporte
 *
 * Diálogo modal para seleccionar una población y obtener su tarifa de transporte.
 * Consulta W094_SGS_TARIFAS_TRANSPORTE según municipios de origen y destino.
 *
 * Uso:
 * ```html
 * <app-buscador-poblacion
 *   (poblacionSeleccionada)="onPoblacionSelected($event)">
 * </app-buscador-poblacion>
 * ```
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-buscador-poblacion',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    TooltipModule,
    BusquedaPaginadorComponent
  ],
  styleUrls: ['../styles/grid-table.styles.scss'],
  template: `
    <p-dialog
      [(visible)]="visible"
      header="Buscar Población"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '48rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '90vw', '768px': '94vw', '640px': '96vw' }"
      [closable]="true"
      [draggable]="false"
      appendTo="body"
    >
      <!-- Filtros de Búsqueda (como en legacy BusquedaGeneralViewImpl) -->
      <div
        class="border border-gray-200 dark:border-gray-600 rounded p-4 mb-4 bg-gray-50 dark:bg-gray-800"
      >
        <h4
          class="text-sm font-semibold text-gray-700 mb-3 border-b dark:border-gray-600 pb-2"
        >
          Filtros de Búsqueda
        </h4>

        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <!-- Campo Codigo -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Codigo</label
            >
            <input
              pInputText
              [(ngModel)]="filtroCodigo"
              (input)="filtroCodigo = sanitizeTextPreserveSpaces(filtroCodigo)"
              placeholder=""
              class="w-full"
              (keyup.enter)="buscar()"
            />
          </div>

          <!-- Campo Nombre -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1"
              >Nombre</label
            >
            <input
              pInputText
              [(ngModel)]="filtroNombre"
              (input)="filtroNombre = sanitizeTextPreserveSpaces(filtroNombre)"
              placeholder=""
              class="w-full"
              (keyup.enter)="buscar()"
            />
          </div>
        </div>

        <!-- Botones Buscar / Limpiar -->
        <div class="flex flex-wrap gap-2 sm:gap-3 justify-end sm:justify-start">
          <button
            pButton
            label="Limpiar"
            icon="pi pi-eraser"
            class="p-button-secondary p-button-outlined min-h-[44px]"
            pTooltip="Limpiar los parámetros ingresados"
            (click)="limpiarFiltros()"
          ></button>
          <button
            pButton
            label="Buscar"
            icon="pi pi-search"
            class="p-button-primary min-h-[44px]"
            pTooltip="Buscar con los parámetros seleccionados en el filtro"
            [loading]="loading()"
            (click)="buscar()"
          ></button>
        </div>
      </div>

      @if (loading()) {
        <div class="md:hidden px-3 pb-3">
          <div
            class="text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            Buscando poblaciones...
          </div>
        </div>
      } @else if (poblaciones().length > 0) {
        <div class="md:hidden space-y-3 p-3">
          <div
            class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
          >
            <span>Listado de Poblaciones</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }} de
              {{ totalRecords() }}
            </span>
          </div>
          @for (poblacion of poblaciones(); track poblacion.cdpoblacion) {
            <div
              class="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
            >
              <div class="mb-3 min-w-0">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  Nombre
                </div>
                <div
                  class="font-semibold text-gray-800 dark:text-gray-100 truncate"
                  [attr.title]="tituloTabla(poblacion.dspoblacion)"
                >
                  {{ textoTabla(poblacion.dspoblacion) }}
                </div>
              </div>
              <div class="grid grid-cols-1 gap-2 text-sm mb-3">
                <div>
                  <span class="text-gray-500 dark:text-gray-400">Código:</span>
                  <span class="text-gray-800 dark:text-gray-200">{{
                    textoTabla(poblacion.cdpoblacion)
                  }}</span>
                </div>
              </div>
              <p-button
                label="Seleccionar"
                icon="pi pi-check"
                size="small"
                styleClass="w-full min-h-[44px]"
                (onClick)="seleccionar(poblacion)"
              />
            </div>
          }
        </div>
      } @else {
        <div class="md:hidden px-3 pb-3">
          <div
            class="text-sm text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-white dark:bg-gray-900"
          >
            @if (!cdsuraOrigen || !cdsuraDestino) {
              Seleccione primero los municipios de origen y destino
            } @else {
              No se encontraron poblaciones para los municipios seleccionados
            }
          </div>
        </div>
      }

      <div class="hidden md:block">
        <div
          role="region"
          [attr.aria-label]="tableConfig().a11y.tableAriaLabel"
          class="sr-only"
        ></div>
        @if (totalRecords() > 0) {
          <div
            class="font-medium text-gray-700 dark:text-gray-200 p-3 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-sm flex justify-between items-center"
          >
            <span>Listado de Poblaciones</span>
            <span class="text-xs text-gray-500 dark:text-gray-400">
              Registros del {{ registroInicio() }} hasta el {{ registroFin() }}
              de {{ totalRecords() }}
            </span>
          </div>
        }
        <p-table
          [resizableColumns]="true"
          columnResizeMode="expand"
          [value]="poblaciones()"
          [loading]="loading()"
          [paginator]="false"
          [scrollable]="true"
          responsiveLayout="scroll"
          [scrollHeight]="tableConfig().responsive.scrollHeight || '300px'"
          [tableStyle]="{ 'min-width': tableConfig().responsive.minWidth || 'max-content' }"
          [styleClass]="tableStyleClass()"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pResizableColumn>
                <span class="grid-header-text" title="Codigo">Codigo</span>
              </th>
              <th pResizableColumn>
                <span class="grid-header-text" title="Nombre">Nombre</span>
              </th>
              <th pResizableColumn class="text-center">
                <span class="grid-header-text" title="Seleccionar"
                  >Seleccionar</span
                >
              </th>
            </tr>
          </ng-template>

          <ng-template pTemplate="body" let-poblacion>
            <tr>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(poblacion.cdpoblacion)"
                  >{{ textoTabla(poblacion.cdpoblacion) }}</span
                >
              </td>
              <td>
                <span
                  class="grid-cell-text"
                  [attr.title]="tituloTabla(poblacion.dspoblacion)"
                  >{{ textoTabla(poblacion.dspoblacion) }}</span
                >
              </td>
              <td class="text-center">
                <button
                  pButton
                  label="Seleccionar"
                  class="p-button-sm p-button-text"
                  (click)="seleccionar(poblacion)"
                ></button>
              </td>
            </tr>
          </ng-template>

          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="3" class="text-center py-8 text-gray-500">
                @if (loading()) {
                  Buscando poblaciones...
                } @else if (!cdsuraOrigen || !cdsuraDestino) {
                  Seleccione primero los municipios de origen y destino
                } @else {
                  No se encontraron poblaciones para los municipios
                  seleccionados
                }
              </td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      @if (busquedaRealizada() && poblaciones().length > 0) {
        <app-busqueda-paginador
          [currentPage]="paginaActual()"
          [totalPages]="totalPages()"
          [hasPrevious]="hasPrevious()"
          [hasNext]="hasNext()"
          [pageSizeOptions]="pageSizeOptions"
          [selectedPageSize]="pageSize()"
          (pageSizeChange)="onPageSizeChange($event)"
          (first)="irPrimeraPagina()"
          (previous)="paginaAnterior()"
          (next)="paginaSiguiente()"
          (last)="irUltimaPagina()"
        ></app-busqueda-paginador>
      }

      <ng-template pTemplate="footer">
        <div class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full">
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="cerrar()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class BuscadorPoblacionComponent {
  private readonly poblacionRepository = inject(PoblacionRepository);

  /** Código SURA del municipio de origen (requerido) - Legacy: getCdsura() */
  @Input() cdsuraOrigen = '';

  /** Código SURA del municipio de destino (requerido) - Legacy: getCdsura() */
  @Input() cdsuraDestino = '';

  /** Evento cuando se selecciona una población */
  @Output() poblacionSeleccionada = new EventEmitter<Poblacion>();

  /** Visibilidad del diálogo */
  visible = false;

  /** Filtro por código (campo1 en legacy) */
  filtroCodigo = '';

  /** Filtro por nombre (campo2 en legacy) */
  filtroNombre = '';

  /** Sanitización XSS en tiempo real (seguridad-girop §3); conserva espacios. */
  readonly sanitizeTextPreserveSpaces = sanitizeTextPreserveSpaces;

  /** Estado de carga */
  loading = signal(false);

  /** Lista de poblaciones */
  poblaciones = signal<Poblacion[]>([]);

  /** Indica si ya se ejecutó una búsqueda */
  busquedaRealizada = signal(false);

  // Paginación por cursor (keyset, ver skill acceso-datos-oracle §7)
  paginaActual = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  totalRecords = signal(0);

  /** totalPages es solo aproximado (para mostrar "Página X de Y") — con
   * keyset no hay conteo exacto de páginas navegables por cursor. */
  readonly totalPages = computed(() => {
    const size = this.pageSize();
    const total = this.totalRecords();
    return size > 0 && total > 0 ? Math.ceil(total / size) : 0;
  });

  // Los botones Anterior/Siguiente dependen de la respuesta del servidor
  // (skill frontend-girop §17.7), no de paginaActual/totalPages.
  hasPrevious = signal(false);
  hasNext = signal(false);

  private nextCursorActual = signal<string | null>(null);
  private previousCursorActual = signal<string | null>(null);

  readonly tableConfig = computed(() =>
    buscadorPoblacionPreset.createConfig({
      data: this.poblaciones() as unknown as Record<string, unknown>[],
      pagination: {
        enabled: false,
        mode: 'server',
        pageSize: this.pageSize()
      },
      style: {
        tableClassName: 'tabla-buscador-poblacion',
        striped: true,
        gridlines: false,
        resizableColumns: true,
        rowHover: false
      },
      state: {
        loading: this.loading(),
        emptyMessage: 'No se encontraron poblaciones'
      },
      responsive: {
        mode: 'hybrid',
        minWidth: 'max-content',
        scrollHeight: '300px',
        cardBreakpointPx: 768
      },
      a11y: {
        tableAriaLabel: 'Tabla de busqueda de poblaciones',
        announceSelection: true,
        announcePageChanges: true,
        focusFirstActionOnRowSelect: false
      }
    })
  );

  readonly tableStyleClass = computed(() => {
    const config = this.tableConfig();
    const classes = ['p-datatable-sm', config.style.tableClassName];
    if (config.style.gridlines !== false) {
      classes.push('p-datatable-gridlines');
    }
    if (config.style.striped !== false) {
      classes.push('p-datatable-striped');
    }
    return classes.join(' ');
  });

  /**
   * Abre el diálogo y realiza búsqueda inicial
   */
  abrir(): void {
    if (!this.cdsuraOrigen || !this.cdsuraDestino) {
      console.warn(
        'BuscadorPoblacion: Se requieren ambos CDSURA de municipios'
      );
      return;
    }
    this.visible = true;
    this.limpiarFiltros();
    this.buscar();
  }

  /**
   * Cierra el diálogo
   */
  cerrar(): void {
    this.visible = false;
  }

  /**
   * Limpia los filtros de búsqueda
   */
  limpiarFiltros(): void {
    this.filtroCodigo = '';
    this.filtroNombre = '';
  }

  /**
   * Realiza la búsqueda de poblaciones
   * Legacy: getParametrosBusquedaPoblacion(codOrigen, codEntrega) donde codOrigen = getCdsura()
   * ORDER BY campo2 (dspoblacion) según legacy
   */
  async buscar(): Promise<void> {
    if (!this.cdsuraOrigen || !this.cdsuraDestino) {
      return;
    }

    // Búsqueda nueva: siempre página 1
    await this.cargarDatos(null, 'NEXT', 1);
  }

  private buildFiltro(): PoblacionFiltro {
    return {
      cdsuraOrigen: this.cdsuraOrigen,
      cdsuraDestino: this.cdsuraDestino,
      // seguridad-girop §3: sanitización XSS del texto libre antes de enviarlo.
      cdpoblacion: sanitizeText(this.filtroCodigo) || undefined,
      dspoblacion: sanitizeText(this.filtroNombre) || undefined
    };
  }

  private async cargarDatos(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    pagina = 1
  ): Promise<void> {
    this.loading.set(true);

    try {
      const response = await firstValueFrom(
        this.poblacionRepository.buscarPoblaciones(
          this.buildFiltro(),
          cursor,
          this.pageSize(),
          direction
        )
      );

      this.poblaciones.set(response.content);
      this.totalRecords.set(response.totalElements);
      this.nextCursorActual.set(response.nextCursor ?? null);
      this.previousCursorActual.set(response.previousCursor ?? null);
      this.hasNext.set(response.hasNext);
      this.hasPrevious.set(response.hasPrevious);
      // La página solo cambia tras una respuesta exitosa. Con LAST se calcula
      // a partir del total que devuelve el servidor.
      this.paginaActual.set(
        direction === 'LAST'
          ? Math.max(1, Math.ceil(response.totalElements / this.pageSize()))
          : pagina
      );
      this.busquedaRealizada.set(true);
    } catch (error) {
      console.error('Error buscando poblaciones:', error);
      this.poblaciones.set([]);
      this.totalRecords.set(0);
    } finally {
      this.loading.set(false);
    }
  }

  registroInicio(): number {
    if (this.totalRecords() === 0) {
      return 0;
    }
    return (this.paginaActual() - 1) * this.pageSize() + 1;
  }

  registroFin(): number {
    if (this.totalRecords() === 0) {
      return 0;
    }
    return Math.min(this.paginaActual() * this.pageSize(), this.totalRecords());
  }

  // Navegación por cursor
  irPrimeraPagina(): void {
    if (this.loading()) {
      return;
    }
    this.cargarDatos(null, 'NEXT', 1);
  }

  paginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) {
      return;
    }
    this.cargarDatos(
      this.previousCursorActual(),
      'PREVIOUS',
      Math.max(1, this.paginaActual() - 1)
    );
  }

  paginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) {
      return;
    }
    this.cargarDatos(this.nextCursorActual(), 'NEXT', this.paginaActual() + 1);
  }

  irUltimaPagina(): void {
    if (this.loading() || this.totalRecords() === 0) {
      return;
    }
    this.cargarDatos(null, 'LAST', this.totalPages());
  }

  onPageSizeChange(newPageSize: number): void {
    const pageSize = Number(newPageSize);
    if (!Number.isFinite(pageSize) || pageSize <= 0 || pageSize === this.pageSize()) {
      return;
    }

    this.pageSize.set(pageSize);
    this.paginaActual.set(1);

    if (this.busquedaRealizada()) {
      this.cargarDatos(null, 'NEXT', 1);
    }
  }

  /**
   * Selecciona una población y emite el evento
   * Legacy: seleccionaronItem(item.getCampo1(), item.getCampo2(), origen)
   */
  seleccionar(poblacion: Poblacion): void {
    this.poblacionSeleccionada.emit(poblacion);
    this.cerrar();
  }

  textoTabla(valor: unknown): string {
    const texto = String(valor ?? '').trim();
    return texto || '-';
  }

  tituloTabla(valor: unknown): string {
    return String(valor ?? '').trim();
  }
}
